import { FieldValues, UseFormReturn } from 'react-hook-form';

export type DevRegisteredFormMethods = UseFormReturn<FieldValues>;

export interface DevRegisteredForm {
  id: string;
  methods: DevRegisteredFormMethods;
}

let armed = false;
let sequence = 0;
let version = 0;
const forms = new Map<string, DevRegisteredFormMethods>();
const listeners = new Set<() => void>();

const notify = (): void => {
  version += 1;
  listeners.forEach((listener) => listener());
};

/**
 * Arms the registry of live `useForm` instances for the whole app. **Opt-in and additive**, the
 * same shape as `installVenueNavigateRouteGuard`: an app that never calls this keeps a registry
 * that is permanently empty, so `registerDevForm` stays a single `false` check and no form's
 * methods are ever referenced.
 *
 * **The caller owns the gate.** This deliberately has no environment check of its own. Deployed
 * bundles are built env-neutral and promoted unchanged, so no build-time value can tell the
 * deployed development environment from production — only the calling app's runtime config can
 * (see Checkd's `dev-tools-enabled`). A guard here could only be `import.meta.env.DEV`, which is
 * false in every deployed environment and so would refuse the one place the registry is wanted.
 *
 * The registry exists so a developer tool that is not part of the page tree — console commands, or
 * anything mounted beside the route outlet — can reach the forms mounted inside it. React context
 * cannot carry that: such a tool is no descendant of any page's `FormProvider`.
 */
export const installDevFormRegistry = (): void => {
  armed = true;
};

export const uninstallDevFormRegistry = (): void => {
  armed = false;
  forms.clear();
  notify();
};

export const registerDevForm = (methods: DevRegisteredFormMethods): (() => void) => {
  if (!armed) return () => {};

  sequence += 1;
  const id = `dev-form-${sequence}`;
  forms.set(id, methods);
  notify();

  return () => {
    forms.delete(id);
    notify();
  };
};

/** Registration order, so the most recently mounted form is last. */
export const getDevForms = (): DevRegisteredForm[] =>
  [...forms.entries()].map(([id, methods]) => ({ id, methods }));

/**
 * A number rather than the form list itself: `useSyncExternalStore` compares snapshots by identity
 * and a fresh array on every read would re-render forever.
 */
export const getDevFormsVersion = (): number => version;

export const subscribeToDevForms = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
